import React from 'react';
import {
  Globe, BarChart3, FileText, Target, TrendingUp,
  Users, Clock, Shield, Sun, Wind, Battery, Layers,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const PortfolioManagementPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Portfolio Management',
      title: (
        <>
          Comprehensive portfolio <em>oversight and reporting</em>
        </>
      ),
      lede: 'Comprehensive portfolio-wide view with streamlined reporting to promote seamless alignment with regulatory requirements and optimize renewable energy operations.',
      image: '/assets/img/general/steptodown.com430799.jpg',
      imageAlt: 'Renewable energy portfolio assets',
      actions: [
        { label: 'Request a walkthrough', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '1GW+', label: 'Portfolio capacity' },
        { value: '500+', label: 'Assets managed' },
        { value: '99.9%', label: 'Uptime monitoring' },
        { value: '24/7', label: 'Portfolio support' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'An advanced portfolio platform',
      lede: 'Our comprehensive portfolio management platform provides all the tools and capabilities needed to optimize your renewable energy investments and ensure regulatory compliance.',
      body: [
        'Comprehensive portfolio management dashboard with real-time analytics and reporting.',
      ],
      checklist: [
        'Comprehensive portfolio-wide performance monitoring',
        'Automated regulatory compliance reporting',
        'Customizable dashboard and data visualization',
        'Real-time performance tracking and analytics',
        'Integration with existing monitoring systems',
        'Multi-stakeholder reporting and communication',
        'Risk assessment and management tools',
        'Performance benchmarking and optimization',
      ],
      image: '/assets/img/general/steptodown.com571771.jpg',
      imageAlt: 'Mixed renewable portfolio under management',
      imageCaption: 'Real-time analytics and reporting across the whole portfolio',
    }}
    capabilities={{
      eyebrow: 'Management platform',
      title: 'Comprehensive management platform',
      lede: 'Advanced portfolio management capabilities to optimize your renewable energy investments.',
      items: [
        {
          icon: Globe,
          title: 'Portfolio overview',
          description: 'Comprehensive portfolio-wide view with real-time performance metrics and asset status across all renewable energy installations.',
        },
        {
          icon: FileText,
          title: 'Regulatory compliance',
          description: 'Streamlined reporting and compliance management to meet regulatory requirements and industry standards.',
        },
        {
          icon: BarChart3,
          title: 'Streamlined reporting',
          description: 'Automated report generation with customizable dashboards and data visualization for stakeholders.',
        },
        {
          icon: Target,
          title: 'Performance tracking',
          description: 'Real-time monitoring and tracking of portfolio performance with detailed analytics and insights.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Specialized portfolio management',
      title: 'Tailored to each asset class',
      lede: 'Tailored portfolio management solutions for different types of renewable energy assets.',
      items: [
        {
          icon: Sun,
          title: 'Solar portfolio management',
          description: 'Comprehensive management of solar energy portfolios with performance optimization and reporting.',
          meta: '500+ assets',
        },
        {
          icon: Wind,
          title: 'Wind portfolio management',
          description: 'Advanced wind farm portfolio management with predictive analytics and maintenance optimization.',
          meta: '100+ assets',
        },
        {
          icon: Layers,
          title: 'Hybrid portfolio management',
          description: 'Integrated management of mixed renewable energy portfolios with cross-technology optimization.',
          meta: '200+ assets',
        },
        {
          icon: Battery,
          title: 'Battery storage management',
          description: 'Specialized management of energy storage systems with grid integration and optimization.',
          meta: '50+ assets',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Transform your portfolio operations',
      title: 'Achieve operational excellence and maximize returns',
      lede: 'Outcomes our comprehensive portfolio management solutions are built to deliver.',
      items: [
        {
          icon: TrendingUp,
          title: 'Optimized performance',
          description: 'Maximize portfolio returns through data-driven insights and performance optimization strategies.',
        },
        {
          icon: Shield,
          title: 'Risk management',
          description: 'Comprehensive risk assessment and mitigation strategies to protect your renewable energy investments.',
        },
        {
          icon: Users,
          title: 'Stakeholder alignment',
          description: 'Seamless coordination between all stakeholders with transparent reporting and communication tools.',
        },
        {
          icon: Clock,
          title: 'Time efficiency',
          description: 'Reduce administrative overhead and save time with automated reporting and management processes.',
        },
      ],
    }}
    cta={{
      title: 'Ready to optimize your portfolio?',
      body: 'Transform your renewable energy portfolio management with our comprehensive platform.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default PortfolioManagementPage;
